/**
 * Iter 192 — Project Stakeholder Matrix + Internal Escalation Ladder.
 *
 * Two panels in one tab:
 *  1. Stakeholders table with an "Add stakeholder" dialog. Picker supports
 *     workspace_member (from workspace users), client_contact (from the
 *     client linked to the project), or free-form external.
 *  2. Internal escalation ladder editor (workspace members only).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, Plus, Trash2, PenSquare, ShieldAlert, ArrowUp, ArrowDown,
  Loader2, UserCircle, Building2, ExternalLink,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";

const ACTOR_TONE = {
  workspace_member: "border-sky-500 text-sky-800 bg-sky-50",
  client_contact: "border-violet-500 text-violet-800 bg-violet-50",
  external: "border-amber-500 text-amber-800 bg-amber-50",
};
const ACTOR_LABEL = {
  workspace_member: "Internal",
  client_contact: "Client",
  external: "External",
};

/* Iter 192 · workspace-member picker helpers.
   /workspaces/{ws}/members returns rows shaped `{user_id, role,
   user: {id, name, email, avatar_data_url, ...}}` — always prefer
   the nested `.user.*` fields, fall back to top-level for legacy shapes. */
const memberLabel = (m) =>
  m?.user?.name || m?.name || m?.user_name || m?.user?.email || m?.email || "—";
const memberEmail = (m) => m?.user?.email || m?.email || "";
const memberId = (m) => m?.user?.id || m?.user_id || m?.id;

export default function ProjectStakeholdersTab({ project }) {
  return (
    <div className="space-y-5" data-testid="project-stakeholders-tab">
      <StakeholderMatrix project={project} />
      <InternalEscalationEditor project={project} />
    </div>
  );
}

/* ─────────────────────────────────────────── */
/*  Stakeholder Matrix                           */
/* ─────────────────────────────────────────── */

function StakeholderMatrix({ project }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${project.id}/stakeholders`);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load stakeholders")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (project?.id) load(); }, [project?.id]);

  const remove = async (id) => {
    if (!window.confirm("Remove this stakeholder from the project?")) return;
    try {
      await api.delete(`/projects/${project.id}/stakeholders/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 bg-muted/20">
        <Users className="w-3.5 h-3.5 text-primary" />
        <span className="text-[10px] uppercase tracking-widest font-semibold">
          Stakeholder matrix
        </span>
        <Badge variant="outline" className="rounded-none text-[10px]">{rows.length}</Badge>
        <span className="text-[10px] text-muted-foreground">
          Everyone with a stake in this project — internal, client-side, and external
        </span>
        <Button size="sm" className="rounded-none h-7 text-xs ml-auto"
                  onClick={() => setAddOpen(true)}
                  data-testid="project-stakeholders-add-btn">
          <Plus className="w-3 h-3 mr-1" /> Add stakeholder
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/20">
            <tr className="text-left">
              <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Name</th>
              <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Type</th>
              <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Project role</th>
              <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Email</th>
              <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Comment</th>
              <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Added</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground italic">
                No stakeholders yet. Click &ldquo;Add stakeholder&rdquo; to seed the matrix.
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/10"
                     data-testid={`project-stakeholder-row-${r.id}`}>
                <td className="p-2 font-medium">
                  <div className="flex items-center gap-1.5">
                    <UserCircle className="w-3.5 h-3.5 text-muted-foreground" />
                    {r.name}
                  </div>
                </td>
                <td className="p-2">
                  <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest ${ACTOR_TONE[r.actor_type] || "border-border"}`}>
                    {ACTOR_LABEL[r.actor_type] || r.actor_type}
                  </span>
                </td>
                <td className="p-2">{r.project_role || "—"}</td>
                <td className="p-2 text-muted-foreground text-[11px]">
                  {r.email ? <a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a> : "—"}
                </td>
                <td className="p-2 text-muted-foreground text-[11px] max-w-xs truncate" title={r.comment || ""}>
                  {r.comment || "—"}
                </td>
                <td className="p-2 text-muted-foreground text-[10px]">
                  {fmtRelative(r.created_at)}
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button type="button" onClick={() => setEditRow(r)}
                              className="inline-flex items-center gap-1 text-primary hover:text-primary/80 text-[11px] mr-2"
                              data-testid={`project-stakeholder-edit-${r.id}`}>
                    <PenSquare className="w-3 h-3" /> Edit
                  </button>
                  <button type="button" onClick={() => remove(r.id)}
                              className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 text-[11px]"
                              data-testid={`project-stakeholder-remove-${r.id}`}>
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StakeholderDialog
        project={project}
        open={addOpen}
        onOpenChange={setAddOpen}
        onSaved={() => { setAddOpen(false); load(); }}
      />
      <StakeholderDialog
        project={project}
        editing={editRow}
        open={!!editRow}
        onOpenChange={(v) => { if (!v) setEditRow(null); }}
        onSaved={() => { setEditRow(null); load(); }}
      />
    </div>
  );
}

function StakeholderDialog({ project, editing, open, onOpenChange, onSaved }) {
  const [actorType, setActorType] = useState("workspace_member");
  const [actorRef, setActorRef] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const [members, setMembers] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setActorType(editing.actor_type);
      setActorRef(editing.actor_ref || "");
      setName(editing.name || "");
      setEmail(editing.email || "");
      setRole(editing.project_role || "");
      setComment(editing.comment || "");
    } else {
      setActorType("workspace_member");
      setActorRef(""); setName(""); setEmail(""); setRole(""); setComment("");
    }
    // Load pickers
    (async () => {
      try {
        const [m, c] = await Promise.all([
          api.get(`/workspaces/${project.workspace_id}/members`)
            .catch(() => ({ data: { members: [] } })),
          project.client_id
            ? api.get(`/clients/${project.client_id}/contacts`)
              .catch(() => ({ data: { rows: [] } }))
            : Promise.resolve({ data: { rows: [] } }),
        ]);
        setMembers(((m.data && (m.data.members || m.data.rows || m.data)) || [])
          .filter(x => x && x.user));
        setContacts((c.data && (c.data.rows || c.data)) || []);
      } catch { /* non-blocking */ }
    })();
  }, [open, editing, project.workspace_id, project.client_id]);

  const applyMember = (uid) => {
    setActorRef(uid);
    const u = members.find(x => memberId(x) === uid);
    if (u) {
      setName(memberLabel(u));
      setEmail(memberEmail(u) || email);
    }
  };
  const applyContact = (cid) => {
    setActorRef(cid);
    const c = contacts.find(x => x.id === cid);
    if (c) { setName(c.name || name); setEmail(c.email || email); }
  };

  const save = async () => {
    if (!name.trim() || !role.trim()) {
      toast.error("Name and project role are required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(
          `/projects/${project.id}/stakeholders/${editing.id}`,
          { name, email, project_role: role, comment });
        toast.success("Updated");
      } else {
        await api.post(`/projects/${project.id}/stakeholders`, {
          actor_type: actorType,
          actor_ref: actorRef || null,
          name, email: email || null,
          project_role: role,
          comment: comment || null,
        });
        toast.success("Stakeholder added");
      }
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="project-stakeholder-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit stakeholder" : "Add stakeholder"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!editing && (
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Type</Label>
              <div className="flex items-center gap-1.5">
                {[
                  { key: "workspace_member", label: "Internal member", icon: UserCircle },
                  { key: "client_contact", label: "Client contact", icon: Building2 },
                  { key: "external", label: "External", icon: ExternalLink },
                ].map(t => (
                  <button key={t.key} type="button"
                              onClick={() => { setActorType(t.key); setActorRef(""); }}
                              className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] ${actorType === t.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
                              data-testid={`stakeholder-type-${t.key}`}>
                    <t.icon className="w-3 h-3" /> {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!editing && actorType === "workspace_member" && (
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Pick a workspace member</Label>
              <select value={actorRef} onChange={(e) => applyMember(e.target.value)}
                         className="h-9 w-full border border-border rounded-none bg-background text-sm px-2"
                         data-testid="stakeholder-member-select">
                <option value="">— select —</option>
                {members.map(m => (
                  <option key={memberId(m)} value={memberId(m)}>
                    {memberLabel(m)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!editing && actorType === "client_contact" && (
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Pick a client contact (or leave blank for free-form)</Label>
              <select value={actorRef} onChange={(e) => applyContact(e.target.value)}
                         className="h-9 w-full border border-border rounded-none bg-background text-sm px-2"
                         data-testid="stakeholder-contact-select">
                <option value="">— free-form —</option>
                {contacts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.title ? ` · ${c.title}` : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Name</Label>
              <Input className="h-8 rounded-none" value={name}
                       onChange={(e) => setName(e.target.value)}
                       data-testid="stakeholder-name" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Email</Label>
              <Input className="h-8 rounded-none" value={email}
                       onChange={(e) => setEmail(e.target.value)}
                       data-testid="stakeholder-email" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Project role</Label>
            <Input className="h-8 rounded-none" value={role}
                     onChange={(e) => setRole(e.target.value)}
                     placeholder="e.g. Approver · Reviewer · SME · Sponsor"
                     data-testid="stakeholder-role" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Comment</Label>
            <Textarea rows={2} className="rounded-none" value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder="Notes on scope, availability, etc."
                          data-testid="stakeholder-comment" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none"
                    onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy}
                    data-testid="stakeholder-save-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {editing ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────── */
/*  Internal Escalation Ladder                    */
/* ─────────────────────────────────────────── */

function InternalEscalationEditor({ project }) {
  const [ladder, setLadder] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [em, wm] = await Promise.all([
        api.get(`/projects/${project.id}/escalation-matrix`),
        api.get(`/workspaces/${project.workspace_id}/members`)
          .catch(() => ({ data: { members: [] } })),
      ]);
      setLadder(em.data.ladder || []);
      setMembers(((wm.data && (wm.data.members || wm.data.rows || wm.data)) || [])
        .filter(x => x && x.user));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load internal ladder")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (project?.id) load(); }, [project?.id]);

  const upd = (i, k, v) => {
    const next = [...ladder]; next[i] = { ...next[i], [k]: v };
    setLadder(next);
  };
  const applyMember = (i, uid) => {
    const u = members.find(x => memberId(x) === uid);
    const next = [...ladder];
    next[i] = {
      ...next[i], user_id: uid,
      name: u ? memberLabel(u) : next[i].name,
      email: (u ? memberEmail(u) : "") || next[i].email,
    };
    setLadder(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= ladder.length) return;
    const next = [...ladder];
    [next[i], next[j]] = [next[j], next[i]];
    setLadder(next.map((s, idx) => ({ ...s, level: idx + 1 })));
  };
  const remove = (i) => setLadder(ladder.filter((_, j) => j !== i)
                              .map((s, idx) => ({ ...s, level: idx + 1 })));
  const add = () => setLadder([...ladder, {
    level: ladder.length + 1, name: "", email: "", user_id: null,
    reason: "", sla_hours: 24,
  }]);

  const save = async () => {
    for (const s of ladder) {
      if (!s.name || !s.name.trim()) { toast.error("Every level needs a name"); return; }
    }
    setBusy(true);
    try {
      const r = await api.put(`/projects/${project.id}/escalation-matrix`,
                                    { ladder });
      setLadder(r.data.ladder);
      toast.success("Internal escalation matrix saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card" data-testid="project-internal-escalation">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 bg-muted/20">
        <ShieldAlert className="w-3.5 h-3.5 text-primary" />
        <span className="text-[10px] uppercase tracking-widest font-semibold">
          Internal escalation ladder
        </span>
        <span className="text-[10px] text-muted-foreground">
          Who inside the team steps in when a delay hits SLA
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                    onClick={add} data-testid="project-escalation-add">
            <Plus className="w-3 h-3 mr-1" /> Add level
          </Button>
          <Button size="sm" className="rounded-none h-7 text-xs"
                    onClick={save} disabled={busy}
                    data-testid="project-escalation-save">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
      <div className="p-3 space-y-2">
        {loading && <div className="text-xs text-muted-foreground text-center p-4">Loading…</div>}
        {!loading && ladder.length === 0 && (
          <div className="text-xs text-muted-foreground italic text-center p-6 border border-dashed border-border">
            No ladder yet. Add L1 → L2 → L3 so teammates know who to loop in when
            work is delayed. Informational for now — auto-notifications land in a future iter.
          </div>
        )}
        {ladder.map((s, i) => (
          <div key={i} className="border border-border bg-white p-2 grid grid-cols-[40px_1.5fr_1fr_1.5fr_80px_auto] gap-1.5 items-end"
                    data-testid={`project-escalation-row-${i}`}>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Level</Label>
              <div className="h-8 flex items-center justify-center font-mono font-bold text-lg border border-primary bg-primary/10 text-primary">
                L{s.level}
              </div>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Team member</Label>
              <select value={s.user_id || ""}
                         onChange={(e) => applyMember(i, e.target.value)}
                         className="h-8 rounded-none border border-border bg-background text-xs px-1 w-full"
                         data-testid={`project-escalation-user-${i}`}>
                <option value="">— pick —</option>
                {members.map(m => (
                  <option key={memberId(m)} value={memberId(m)}>
                    {memberLabel(m)}
                  </option>
                ))}
              </select>
              <Input value={s.name || ""}
                       onChange={(e) => upd(i, "name", e.target.value)}
                       className="h-7 rounded-none text-xs mt-1"
                       placeholder="Name (auto-filled)" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Email</Label>
              <Input value={s.email || ""}
                       onChange={(e) => upd(i, "email", e.target.value)}
                       className="h-8 rounded-none text-xs" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Reason to escalate</Label>
              <Input value={s.reason || ""}
                       onChange={(e) => upd(i, "reason", e.target.value)}
                       className="h-8 rounded-none text-xs"
                       placeholder="e.g. Blocker &gt; 4h" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">SLA (hrs)</Label>
              <Input type="number" min={0}
                       value={s.sla_hours ?? ""}
                       onChange={(e) => upd(i, "sla_hours", e.target.value === "" ? null : Number(e.target.value))}
                       className="h-8 rounded-none text-xs font-mono" />
            </div>
            <div className="flex flex-col gap-1">
              <button type="button" onClick={() => move(i, -1)}
                          disabled={i === 0}
                          className="h-3.5 text-muted-foreground hover:text-primary disabled:opacity-30"
                          data-testid={`project-escalation-up-${i}`}>
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => move(i, 1)}
                          disabled={i === ladder.length - 1}
                          className="h-3.5 text-muted-foreground hover:text-primary disabled:opacity-30"
                          data-testid={`project-escalation-down-${i}`}>
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => remove(i)}
                          className="h-3.5 text-rose-600 hover:text-rose-700"
                          data-testid={`project-escalation-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
